/**
 * Real MG dataset loader. Fetches the JSON artifacts produced by the
 * Python ETL (mockup/ingest/build_mg_*.py) from /public/data/.
 *
 * These are served statically by Vite at `/data/<file>.json`.
 */
import { useEffect, useState } from "react";

// ------- Types match the ETL output shapes -------
export type BrandAwarenessRow = {
  brand: string;
  metric: "total_aware" | "ever_used" | "currently_using";
  count: number;
  pct: number;
  var: string;
};

export type WaveMetrics = {
  wave: string;
  n_total: number;
  brand_awareness: BrandAwarenessRow[];
  brand_awareness_metric: string | null;
  mg_ownership: { var: string; count: number; pct: number } | null;
  city_distribution: { code: string; n: number; pct: number }[] | null;
  demographics: {
    age?: { mean: number; median: number; n: number };
    gender?: { code: string; n: number }[];
    sec?: { code: string; n: number }[];
  };
};

export type ImageryStatement = { id: string; label: string };
export type BrandImagery = {
  brands: string[];
  statements: ImageryStatement[];
  matrix: Record<string, Record<string, number | null>>;
  note: string;
};

export type MgQuant = {
  meta: {
    subject: string;
    subject_code: string;
    waves: WaveMetrics[];
    total_respondents: number;
    brand_order: string[];
  };
  brand_awareness_by_wave: {
    wave1: { metric: string; rows: BrandAwarenessRow[] };
    wave2: { metric: string; rows: BrandAwarenessRow[] };
  };
  mg_ownership_by_wave: { wave1: any; wave2: any };
  city_distribution: { wave1: any; wave2: any };
  demographics: { wave1: any; wave2: any };
  brand_imagery_wave1: BrandImagery;
};

export type ThemeRankRow = {
  theme: string;
  respondents_mentioning: number;
  total_mentions: number;
  by_city: Record<string, number>;
};

export type Interview = {
  file: string;
  di: string;
  respondent: string;
  city: string;
  moderator: string;
  turns_total: number;
  turns_R: number;
  respondent_word_count: number;
  themes: Record<string, number>;
};

export type MgQual = {
  meta: {
    total_interviews: number;
    cities: string[];
    moderators: string[];
    total_respondent_words: number;
  };
  interviews: Interview[];
  theme_rank: ThemeRankRow[];
  cooccurrence?: {
    themes: string[];
    matrix: Record<string, Record<string, number>>;
  };
};

export type Quote = {
  theme: string;
  text: string;
  respondent: string;
  di: string;
  city: string;
  moderator: string;
  turn: number;
  words: number;
};

export type QuoteBank = {
  total_quotes: number;
  by_theme_count: Record<string, number>;
  quotes: Quote[];
};

export type PsychoSegment = {
  id: number;
  label: string;
  n: number;
  pct: number;
  centroid: [number, number];
  top_statements_pos: { id: string; label: string; z: number }[];
  top_statements_neg: { id: string; label: string; z: number }[];
};

export type CrosstabCell = { centre?: string; segment?: number; brand: string; count: number; pct: number };

export type MgPsycho = {
  n: number;
  statements: { id: string; label: string }[];
  var_explained: [number, number];
  loadings: Record<string, [number, number]>;
  points: { pc1: number; pc2: number; segment: number }[];
  segments: PsychoSegment[];
  top_brands: string[];
  city_brand: {
    centres: string[];
    totals: Record<string, number>;
    rows: CrosstabCell[];
  };
  segment_brand: {
    totals: Record<string, number>;
    rows: CrosstabCell[];
  };
};

// ------- v3 attribute drivers / cross-category / Crompton lens -------
export type ImportanceRow = {
  code: string;
  label: string;
  var: string;
  mean: number | null;
  ci_low: number | null;
  ci_high: number | null;
  std: number | null;
  n: number;
};

export type AttributeDrivers = {
  categories: {
    MG:   { subject: string; n: number; functional: ImportanceRow[]; emotional: ImportanceRow[]; brand: ImportanceRow[] };
    Fans: { subject: string; n: number; functional: ImportanceRow[]; emotional: ImportanceRow[]; brand: ImportanceRow[] };
  };
};

export type CommonAttributePair = {
  label: string; mg_code: string; fans_code: string;
  mg_mean: number; fans_mean: number; mg_n: number; fans_n: number;
};

export type FunctionalTheme = {
  id: string; label: string;
  mg_codes: string[]; fans_codes: string[];
  mg_mean: number | null; mg_n: number;
  fans_mean: number | null; fans_n: number;
  common_score: number | null;
};

export type CrossCategory = {
  common_emotional: CommonAttributePair[];
  common_brand: CommonAttributePair[];
  functional_themes: FunctionalTheme[];
  note: string;
};

export type LensRow = {
  code: string; label: string;
  importance: number | null;
  ci_low: number | null;
  ci_high: number | null;
  n: number;
  crompton_share: number | null;
  top_competitor: string | null;
  top_competitor_share: number | null;
  all_competitor_shares: { brand: string; pct: number | null }[];
  quadrant: "owned_strength" | "opportunity" | "owned_low_value" | "low_priority" | "missing_data";
};

export type LensBlock = {
  brand: string;
  competitors: string[];
  imp_median: number;
  own_median: number;
  rows: LensRow[];
};

export type LatentTheme = {
  id: string; label: string; narrative: string;
  mg_members: { code: string; label: string }[];
  fans_members: { code: string; label: string }[];
  mg_avg_corr: number;
  fans_avg_corr: number;
  mg_evidence: { a: string; b: string; r: number }[];
  fans_evidence: { a: string; b: string; r: number }[];
  validated: boolean;
};

export type PremiumLiftRow = {
  layer: "emotional" | "brand";
  label: string;
  mg_full: number; mg_premium: number; mg_lift: number;
  fans_full: number; fans_premium: number; fans_lift: number;
  joint_lift: number;
  n_mg_premium: number; n_fans_premium: number;
};

export type StretchRow = {
  label: string;
  mg_code: string; fans_code: string;
  importance_mg: number | null; importance_fans: number | null;
  crompton_mg: number | null; crompton_fans: number | null;
  bajaj_mg: number | null; bajaj_fans: number | null;
  crompton_index_mg: number | null; crompton_index_fans: number | null;
  index_gap: number | null;
  stretch_class: "consistent" | "stretch" | "severe" | "missing";
};

export type Revelation = {
  id: string;
  headline: string;
  body: string;
  evidence: string;
  why_hidden: string;
  action: string;
};

export type Connections = {
  meta: { mg_n: number; fans_n: number; mg_premium_n: number; fans_premium_n: number };
  latent_themes: LatentTheme[];
  premium_persona: { summary: string; rows: PremiumLiftRow[] };
  brand_stretch: {
    summary: string;
    method_note: string;
    rows: StretchRow[];
    crompton_consistent_count: number;
    crompton_stretch_count: number;
  };
  revelations: Revelation[];
};

export type CromptonLens = {
  categories: {
    MG: {
      subject: string; n: number;
      brand: LensBlock | null;
      functional: LensBlock | null;
      emotional: LensBlock | null;
      note?: string;
    };
    Fans: {
      subject: string; n: number;
      brand: LensBlock | null;
      functional: LensBlock | null;
      emotional: LensBlock | null;
    };
  };
};

// ------- In-memory caches (one fetch per session) -------
let quantCache: MgQuant | null = null;
let qualCache: MgQual | null = null;
let bankCache: QuoteBank | null = null;
let psychoCache: MgPsycho | null = null;
let driversCache: AttributeDrivers | null = null;
let crossCache: CrossCategory | null = null;
let lensCache: CromptonLens | null = null;
let connectionsCache: Connections | null = null;

async function load<T>(path: string, cache: T | null, setCache: (v: T) => void): Promise<T> {
  if (cache) return cache;
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  const data: T = await res.json();
  setCache(data);
  return data;
}

export function useMgQuant(): MgQuant | null {
  const [data, setData] = useState<MgQuant | null>(quantCache);
  useEffect(() => {
    if (!data) {
      load<MgQuant>("/data/mg_quant.json", quantCache, (v) => { quantCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useMgQual(): MgQual | null {
  const [data, setData] = useState<MgQual | null>(qualCache);
  useEffect(() => {
    if (!data) {
      load<MgQual>("/data/mg_qual.json", qualCache, (v) => { qualCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useQuoteBank(): QuoteBank | null {
  const [data, setData] = useState<QuoteBank | null>(bankCache);
  useEffect(() => {
    if (!data) {
      load<QuoteBank>("/data/mg_quote_bank.json", bankCache, (v) => { bankCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useMgPsycho(): MgPsycho | null {
  const [data, setData] = useState<MgPsycho | null>(psychoCache);
  useEffect(() => {
    if (!data) {
      load<MgPsycho>("/data/mg_psychographic.json", psychoCache, (v) => { psychoCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useDrivers(): AttributeDrivers | null {
  const [data, setData] = useState<AttributeDrivers | null>(driversCache);
  useEffect(() => {
    if (!data) {
      load<AttributeDrivers>("/data/attribute_drivers.json", driversCache, (v) => { driversCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useCrossCategory(): CrossCategory | null {
  const [data, setData] = useState<CrossCategory | null>(crossCache);
  useEffect(() => {
    if (!data) {
      load<CrossCategory>("/data/cross_category.json", crossCache, (v) => { crossCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useCromptonLens(): CromptonLens | null {
  const [data, setData] = useState<CromptonLens | null>(lensCache);
  useEffect(() => {
    if (!data) {
      load<CromptonLens>("/data/crompton_lens.json", lensCache, (v) => { lensCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

export function useConnections(): Connections | null {
  const [data, setData] = useState<Connections | null>(connectionsCache);
  useEffect(() => {
    if (!data) {
      load<Connections>("/data/connections.json", connectionsCache, (v) => { connectionsCache = v; })
        .then(setData).catch(console.error);
    }
  }, [data]);
  return data;
}

// ------- Derived helpers -------

/** Total respondent count across both waves. */
export function totalRespondents(q: MgQuant): number {
  return q.meta.total_respondents;
}

/** Top N brands by the chosen awareness/usage metric for the named wave. */
export function topBrands(q: MgQuant, wave: "wave1" | "wave2", n = 10): BrandAwarenessRow[] {
  return (q.brand_awareness_by_wave[wave]?.rows ?? []).slice(0, n);
}

/** Themes ranked across all 18 DIs; returns the top N with counts. */
export function topThemes(q: MgQual, n = 8): ThemeRankRow[] {
  return q.theme_rank.filter((t) => t.respondents_mentioning > 0).slice(0, n);
}

/** Pick N quotes for a theme, preferring mid-length ones. */
export function quotesForTheme(bank: QuoteBank, theme: string, n = 3): Quote[] {
  return bank.quotes
    .filter((q) => q.theme === theme)
    .sort((a, b) => Math.abs(a.words - 30) - Math.abs(b.words - 30))
    .slice(0, n);
}
